import React, {isValidElement, useEffect, useState} from 'react';

import {FormProvider, useForm, UseFormReturn} from 'react-hook-form';
import {handleGetInput} from './util/handle-get-input';

import {UseMutationResult} from 'react-query';
import {NestedKeys} from 'shared/types/key-manipulation';
import {InformationKeysProps} from 'shared/util/information-props';
import {TranslationKeyProps} from 'shared/util/translated-props';
import Button, {ButtonProps} from '../button';
import {Container, Inputs, Row} from './styles';
import useFormInputs from './util';

export interface BaseInputProps<T = any> {
  name?: string;
  label?: string;
  placeholder?: string;
  type:
    | 'section'
    | 'dynamic-section'
    | 'input'
    | 'input-button'
    | 'text-area'
    | 'select'
    | 'multi-select'
    | 'date-picker'
    | 'time-picker'
    | 'address-input'
    | 'file-select'
    | 'switch'
    | 'async-select'
    | 'number-input'
    | 'audio-recorder'
    | 'video-input'
    | 'list-section'
    | 'async-multi-select'
    | 'text-section'
    | 'inline-code'
    | 'image-picker'
    | 'custom'
    | 'price-input'
    | 'dynamic-table';
  border?: boolean;
  translationKey?: TranslationKeyProps;
  disabled?: boolean;
  hidden?: boolean | ((data: T) => boolean);
  information?: string;
  required?: boolean | ((data: T) => boolean);
}

export type InputProps<T = any> = NestedKeys<T> | (() => BaseInputProps<T>);

export interface FormProps<T> {
  inputs: InputProps<T>[];
  onSubmit?: (
    defaultValues: any,
  ) => UseMutationResult<any, unknown, any, unknown>;
  handleSubmit?: (data: any) => any;
  onFormStateChange?: (data: T) => void;
  defaultValues?: any;
  translationKey: TranslationKeyProps;
  informationKey?: InformationKeysProps;
  border?: boolean;
  preMutateCheck?: (data: T, mutate: () => void) => void;
  extraButtons?:
    | ((data: T) => (ButtonProps | React.ReactNode)[])
    | (ButtonProps | React.ReactNode)[];
  disabled?: boolean;
  buttonProps?: Partial<ButtonProps>;
  formContext?: UseFormReturn<any, any>;
}

const Form: <T = any>(p: FormProps<T>) => React.ReactElement<T> = ({
  inputs,
  onSubmit,
  handleSubmit,
  onFormStateChange,
  defaultValues,
  translationKey,
  informationKey,
  border = false,
  preMutateCheck,
  extraButtons = [],
  disabled = false,
  buttonProps,
  ...props
}) => {
  const formContext = props.formContext ?? useForm({defaultValues});
  const {getInput} = useFormInputs();
  const [reorderModal, showReorderModal] = useState(false);
  const {mutate, isLoading} = onSubmit
    ? onSubmit(defaultValues)
    : ({} as UseMutationResult<any, unknown, any, unknown>);

  const formState = formContext.watch();

  useEffect(() => {
    if (onFormStateChange) onFormStateChange(formState);
  }, [formState]);

  const renderInput = (input: NestedKeys<any> | (() => BaseInputProps)) => {
    if (typeof input === 'string')
      return handleGetInput(
        getInput({
          name: input,
          border,
          disabled,
        })(),
        translationKey,
        informationKey,
      );

    const {required} = input();

    const inputData = {
      ...input(),
      border: input().border ?? border,
      disabled: disabled === true ? true : input().disabled,
      required: required
        ? typeof required === 'boolean'
          ? required
          : required(formState)
        : undefined,
    };

    if (
      (typeof inputData.hidden === 'function' && inputData.hidden(formState)) ||
      (typeof inputData.hidden === 'boolean' && inputData.hidden === false)
    )
      return null;

    return handleGetInput(inputData, translationKey, informationKey);
  };

  return (
    <FormProvider {...formContext}>
      <Container>
        <Inputs>{inputs.map(renderInput)}</Inputs>
        {!disabled && (
          <Row>
            {onSubmit && (
              <Button
                title="Salvar"
                onClick={() => {
                  const data = formContext.getValues();

                  if (preMutateCheck) {
                    return preMutateCheck(data, () =>
                      mutate(handleSubmit ? handleSubmit(data) : data),
                    );
                  }

                  mutate(handleSubmit ? handleSubmit(data) : data);
                }}
                {...{loading: isLoading}}
                {...(buttonProps ?? {})}
              />
            )}
            {(!Array.isArray(extraButtons)
              ? extraButtons(formContext.getValues())
              : extraButtons
            ).map((button, index) => {
              if (isValidElement(button)) return button;

              return <Button key={index} {...(button as ButtonProps)} />;
            })}
          </Row>
        )}
      </Container>
    </FormProvider>
  );
};

export default Form;
